import type en from '../en/templates'
import type { Translation } from '../types'

/** Configuration templates: the list, the wizard and the blueprints it renders */
const templates: Translation<typeof en> = {
  "templates.title": "Vorlagen",
  "templates.subtitle": "Wiederverwendbare Konfiguration für Ihre Standorte",
  "templates.new": "Neue Vorlage",
  "templates.allTypes": "Alle Typen",
  "templates.empty": "Noch keine Vorlagen",
  "templates.emptyText": "Erstellen Sie eine Vorlage, indem Sie die Konfiguration eines bestehenden Standorts übernehmen, oder bauen Sie selbst eine.",
  "templates.createFirst": "Erste Vorlage erstellen",
  "templates.family.ANY": "Jedes Gerät",
  "templates.defaultBadge": "STANDARD",
  "templates.origin.PULLED": "übernommen",
  "templates.origin.MANUAL": "erstellt",
  "templates.system": "System",
  "templates.applications": { one: "{count}-mal angewendet", other: "{count}-mal angewendet" },
  "templates.editHint": "Vorlage und Konfiguration bearbeiten",
  "templates.apply": "Anwenden",
  "templates.deleteNamed": "{name} löschen",
  "templates.deleteConfirm": "Vorlage „{name}“ löschen?",
  "templates.applyTitle": "Vorlage anwenden",
  "templates.queued": { one: "Für {count} Standort eingeplant", other: "Für {count} Standorte eingeplant" },
  "templates.queuedText": "Jeder Standort meldet, welche Felder angewendet wurden. Nicht unterstützte Felder werden festgehalten, nicht stillschweigend verworfen.",
  "templates.done": "Fertig",
  "templates.targets": "„{name}“ ist für {family}-Geräte bestimmt.",
  "templates.noCompatible": "Kein verbundener Standort kann diese Vorlage übernehmen. Prüfen Sie, für welche Gerätefamilie sie gedacht ist.",
  "templates.partial": { one: "{count} gewählter Standort überspringt einige Felder:", other: "{count} gewählte Standorte überspringen einige Felder:" },
  "templates.more": "+{count} weitere",
  "templates.showIncompatible": { one: "{count} Standort anzeigen, der sie nicht übernehmen kann", other: "{count} Standorte anzeigen, die sie nicht übernehmen können" },
  "templates.hideIncompatible": { one: "{count} Standort ausblenden, der sie nicht übernehmen kann", other: "{count} Standorte ausblenden, die sie nicht übernehmen können" },
  "templates.notLicensed": "nicht in Ihrer Lizenz",
  "templates.noSupported": "keine unterstützten Felder",
  "templates.wrongFamily": "andere Gerätefamilie",
  "templates.applyTo": { one: "Auf {count} Standort anwenden", other: "Auf {count} Standorte anwenden" },
  "templates.allSupported": "Alle Felder werden unterstützt",
  "templates.skipped": { one: "{count} Feld wird übersprungen", other: "{count} Felder werden übersprungen" },
  "templates.family.UNIFI": "Controller und Site Manager",
  "templates.family.VELANOX": "One Speed gebündeltes WAN",
  "templates.family.TELTONIKA": "RutOS-Mobilfunkrouter",
  "templates.family.PEPLINK": "Balance / MAX mit SpeedFusion",
  "templates.family.MIKROTIK": "RouterOS-Router und PtP-/PtMP-Funkstrecken",
  "templates.saveFailed": "Die Vorlage konnte nicht gespeichert werden",
  "templates.pullFailed": "Die Konfiguration konnte nicht übernommen werden",
  "templates.updated": "Vorlage aktualisiert",
  "templates.saved": "Vorlage gespeichert",
  "templates.viewAll": "Vorlagen ansehen",
  "templates.createAnother": "Weitere erstellen",
  "templates.back": "Zurück",
  "templates.editing": "Bearbeiten: {name}",
  "templates.whichKind": "Für welche Art von Gerät ist das?",
  "templates.sitesConnected": { one: "{count} Standort verbunden", other: "{count} Standorte verbunden" },
  "templates.named": "Vorlage {name}",
  "templates.whatConfigure": "Was möchten Sie konfigurieren?",
  "templates.variant": "Zielvariante",
  "templates.variantHint": "Über die Cloud verwaltete Varianten unterstützen weniger Änderungen als der direkte Zugriff auf das Gerät.",
  "templates.loadingTypes": "Vorlagentypen werden geladen…",
  "templates.howCreate": "Wie möchten Sie sie erstellen?",
  "templates.buildWizard": "Mit dem Assistenten erstellen",
  "templates.buildHint": "Schritt für Schritt, nur mit dem, was {family}-Geräte unterstützen.",
  "templates.pullFromSite": "Von einem Standort übernehmen",
  "templates.noPullSites": "Noch keine Standorte mit UniFi-Controller verbunden.",
  "templates.pullOnlyFor": "Verfügbar für Netzwerk-, WLAN- und WAN-Vorlagen von einem Standort mit UniFi-Controller.",
  "templates.pullFrom": { one: "Übernehmen Sie die aktuelle Konfiguration Ihres Standorts mit UniFi-Controller.", other: "Übernehmen Sie die aktuelle Konfiguration eines Ihrer {count} Standorte mit UniFi-Controller." },
  "templates.sourceSite": "Quellstandort *",
  "templates.chooseSite": "Wählen Sie einen Standort…",
  "templates.name": "Name der Vorlage *",
  "templates.description": "Beschreibung",
  "templates.optional": "Optional",
  "templates.optionalParen": "(optional)",
  "templates.pullConfig": "Konfiguration übernehmen",
  "templates.coverage": "{provider} unterstützt {count} von {total} Optionen für diesen Vorlagentyp.",
  "templates.hiddenSteps": { one: "{count} Schritt ist ganz ausgeblendet.", other: "{count} Schritte sind ganz ausgeblendet." },
  "templates.stepOf": "Schritt {n} von {total}",
  "templates.nothingHere": "In diesem Schritt gibt es für {provider} nichts zu konfigurieren.",
  "templates.showUnsupported": { one: "{count} nicht unterstützte Option anzeigen", other: "{count} nicht unterstützte Optionen anzeigen" },
  "templates.hideUnsupported": { one: "{count} nicht unterstützte Option ausblenden", other: "{count} nicht unterstützte Optionen ausblenden" },
  "templates.saveTemplate": "Vorlage speichern",
  "templates.updateTemplate": "Vorlage aktualisieren",
  "templates.myName": "Mein {name}",
  "templates.useSuggestion": "„{name}“ verwenden",
  "templates.next": "Weiter",
  "templates.notSupported": "nicht unterstützt",
  "templates.enabled": "Aktiviert",
  "templates.disabled": "Deaktiviert",
  "templates.choose": "Wählen…",
  "templates.typeEnter": "Eingeben und Enter drücken",
  "templates.add": "Hinzufügen",
  "templates.removeNamed": "{name} entfernen",
  "templates.inUse": "belegt: {list}",
  "templates.type.NETWORK": "Netzwerk / VLAN",
  "templates.typeDesc.NETWORK": "VLAN-Segmentierung, DHCP-Bereich und Isolation",
  "templates.step.NETWORK.identity": "Zweck und Identität",
  "templates.stepDesc.NETWORK.identity": "Wofür dieses Netzwerk da ist",
  "templates.field.NETWORK.purpose": "Zweck",
  "templates.option.NETWORK.purpose.production": "🏭 Produktion",
  "templates.option.NETWORK.purpose.cash-registers": "🏪 Kassen",
  "templates.option.NETWORK.purpose.entrance": "🚪 Eingang / Lobby",
  "templates.option.NETWORK.purpose.cp": "🖥️ CP / Bedienfeld",
  "templates.option.NETWORK.purpose.employers": "👔 Mitarbeitende",
  "templates.option.NETWORK.purpose.media": "📺 Medien / AV",
  "templates.option.NETWORK.purpose.iot": "📡 IoT-Geräte",
  "templates.option.NETWORK.purpose.guest": "🌐 Gäste / Besucher",
  "templates.option.NETWORK.purpose.vlan-only": "🔒 Nur VLAN (kein DHCP)",
  "templates.field.NETWORK.name": "Netzwerkname",
  "templates.field.NETWORK.vlanId": "VLAN ID",
  "templates.help.NETWORK.vlanId": "Vorgeschlagen anhand der VLANs, die Ihre Vorlagen bereits nutzen",
  "templates.step.NETWORK.addressing": "IP-Adressierung und DHCP",
  "templates.stepDesc.NETWORK.addressing": "Subnetz und Adressbereich",
  "templates.field.NETWORK.subnet": "Subnetz",
  "templates.help.NETWORK.subnet": "z. B. 192.168.30.0/24",
  "templates.field.NETWORK.dhcp.enabled": "DHCP-Server",
  "templates.field.NETWORK.dhcp.start": "DHCP-Start",
  "templates.field.NETWORK.dhcp.end": "DHCP-Ende",
  "templates.field.NETWORK.dhcp.dns": "DNS-Server",
  "templates.step.NETWORK.security": "Sicherheit",
  "templates.stepDesc.NETWORK.security": "Isolation und Schutz",
  "templates.field.NETWORK.isolation": "Client-Isolation",
  "templates.help.NETWORK.isolation": "Verhindert, dass Geräte in diesem Netzwerk miteinander kommunizieren",
  "templates.field.NETWORK.dhcpGuard": "DHCP-Schutz",
  "templates.help.NETWORK.dhcpGuard": "Blockiert unerwünschte DHCP-Server",
  "templates.field.NETWORK.igmpSnooping": "IGMP snooping",
  "templates.type.WIFI": "WLAN-Netzwerk",
  "templates.typeDesc.WIFI": "SSID, Sicherheit und Funkverhalten",
  "templates.step.WIFI.identity": "SSID und Identität",
  "templates.stepDesc.WIFI.identity": "Name und Sichtbarkeit",
  "templates.field.WIFI.ssid": "SSID",
  "templates.field.WIFI.enabled": "Netzwerk aktiv",
  "templates.field.WIFI.isHidden": "Verborgene SSID",
  "templates.field.WIFI.isGuest": "Gastnetzwerk",
  "templates.help.WIFI.isGuest": "Isoliert Clients vom LAN",
  "templates.step.WIFI.security": "Sicherheit",
  "templates.stepDesc.WIFI.security": "Verschlüsselung und Zugangsdaten",
  "templates.field.WIFI.security": "Sicherheitstyp",
  "templates.option.WIFI.security.wpa3": "WPA3-Personal (empfohlen)",
  "templates.option.WIFI.security.wpa2": "WPA2-Personal (kompatibel)",
  "templates.option.WIFI.security.wpa2-enterprise": "WPA2-Enterprise (RADIUS)",
  "templates.option.WIFI.security.open": "Offen – kein Passwort",
  "templates.field.WIFI.passphrase": "Passwort",
  "templates.help.WIFI.passphrase": "Mindestens 8 Zeichen",
  "templates.field.WIFI.vlanId": "VLAN-Tag",
  "templates.help.WIFI.vlanId": "Leer lässt die SSID ohne Tag",
  "templates.step.WIFI.radio": "Funk und Leistung",
  "templates.stepDesc.WIFI.radio": "Bänder und Roaming",
  "templates.field.WIFI.band": "Band",
  "templates.option.WIFI.band.both": "2.4 + 5 GHz",
  "templates.option.WIFI.band.5g": "Nur 5 GHz",
  "templates.option.WIFI.band.2g": "Nur 2,4 GHz",
  "templates.option.WIFI.band.6g": "6 GHz (WLAN 6E)",
  "templates.field.WIFI.txPower": "Sendeleistung",
  "templates.option.WIFI.txPower.auto": "Automatisch",
  "templates.option.WIFI.txPower.low": "Niedrig",
  "templates.option.WIFI.txPower.medium": "Mittel",
  "templates.option.WIFI.txPower.high": "Hoch",
  "templates.field.WIFI.fastRoaming": "Schnelles Roaming (802.11r)",
  "templates.field.WIFI.minRssi": "Mindestsignal",
  "templates.field.WIFI.scheduleEnabled": "Zeitplan aktiv",
  "templates.type.WAN": "WAN / Internet",
  "templates.typeDesc.WAN": "Verhalten der Uplinks, Failover und Lastverteilung",
  "templates.step.WAN.links": "Verhalten der Uplinks",
  "templates.stepDesc.WAN.links": "Wie die WAN-Verbindungen genutzt werden",
  "templates.field.WAN.failoverEnabled": "Failover",
  "templates.help.WAN.failoverEnabled": "Auf eine Ersatzverbindung umschalten, wenn die primäre ausfällt",
  "templates.field.WAN.loadBalancingEnabled": "Lastverteilung",
  "templates.help.WAN.loadBalancingEnabled": "Verteilt Sitzungen auf die aktiven Verbindungen",
  "templates.field.WAN.wanPortCount": "Anzahl der WAN-Verbindungen",
  "templates.option.WAN.wanPortCount.2": "2",
  "templates.option.WAN.wanPortCount.3": "3",
  "templates.option.WAN.wanPortCount.4": "4",
  "templates.field.WAN.healthCheckEnabled": "Zustandsprüfung",
  "templates.help.WAN.healthCheckEnabled": "Prüft jede Verbindung und markiert sie bei einem Fehler als ausgefallen",
  "templates.field.WAN.priorityMode": "Prioritätsmodus",
  "templates.option.WAN.priorityMode.ordered": "Feste Reihenfolge",
  "templates.option.WAN.priorityMode.weighted": "Gewichtet",
  "templates.step.WAN.quality": "Dienstgüte",
  "templates.stepDesc.WAN.quality": "Warteschlangenverwaltung und MTU",
  "templates.field.WAN.smartQueueEnabled": "Smart Queue (QoS)",
  "templates.help.WAN.smartQueueEnabled": "Verringert Bufferbloat unter Last",
  "templates.field.WAN.smartQueueAutoDetect": "Geschwindigkeit beim Ausrollen automatisch erkennen",
  "templates.help.WAN.smartQueueAutoDetect": "Führt beim Anwenden einen Geschwindigkeitstest aus und verwendet 90 % des Ergebnisses",
  "templates.field.WAN.smartQueueDown": "Download-Geschwindigkeit",
  "templates.field.WAN.smartQueueUp": "Upload-Geschwindigkeit",
  "templates.field.WAN.mtu": "MTU",
  "templates.type.CELLULAR": "Mobilfunk / SIM",
  "templates.typeDesc.CELLULAR": "APN, SIM-Richtlinie, Datenlimits und Roaming",
  "templates.step.CELLULAR.carrier": "Anbieter und APN",
  "templates.stepDesc.CELLULAR.carrier": "Wie sich das Modem im Netz anmeldet",
  "templates.field.CELLULAR.apn": "APN",
  "templates.help.CELLULAR.apn": "Leer lassen für die automatische Erkennung durch den Anbieter",
  "templates.field.CELLULAR.apnUser": "APN-Benutzername",
  "templates.field.CELLULAR.apnPassword": "APN-Passwort",
  "templates.field.CELLULAR.pin": "SIM-PIN",
  "templates.step.CELLULAR.sim": "SIM-Richtlinie",
  "templates.stepDesc.CELLULAR.sim": "Welche SIM genutzt wird und wann gewechselt wird",
  "templates.field.CELLULAR.simSwitch": "Bevorzugter SIM-Steckplatz",
  "templates.option.CELLULAR.simSwitch.1": "SIM 1",
  "templates.option.CELLULAR.simSwitch.2": "SIM 2",
  "templates.field.CELLULAR.simFailover": "Bei Ausfall automatisch wechseln",
  "templates.help.CELLULAR.simFailover": "Wechselt zur anderen SIM, wenn die Verbindung abbricht",
  "templates.field.CELLULAR.roaming": "Roaming erlauben",
  "templates.help.CELLULAR.roaming": "Kann außerhalb des Heimnetzes teuer sein",
  "templates.step.CELLULAR.limits": "Daten und Funk",
  "templates.stepDesc.CELLULAR.limits": "Limits und Bandauswahl",
  "templates.field.CELLULAR.dataLimit": "Monatliches Datenlimit",
  "templates.help.CELLULAR.dataLimit": "Bei 90 % davon gibt es eine Meldung",
  "templates.field.CELLULAR.dataLimitAction": "Wenn das Limit erreicht ist",
  "templates.option.CELLULAR.dataLimitAction.alert": "Nur melden",
  "templates.option.CELLULAR.dataLimitAction.throttle": "Drosseln",
  "templates.option.CELLULAR.dataLimitAction.disconnect": "Trennen",
  "templates.field.CELLULAR.bandLock": "Bandsperre",
  "templates.help.CELLULAR.bandLock": "Bandnummern durch Kommas getrennt, leer = automatisch",
  "templates.field.CELLULAR.networkMode": "Netzmodus",
  "templates.option.CELLULAR.networkMode.auto": "Automatisch",
  "templates.option.CELLULAR.networkMode.5g": "5G bevorzugt",
  "templates.option.CELLULAR.networkMode.lte": "Nur LTE",
  "templates.option.CELLULAR.networkMode.3g": "3G als Rückfall",
  "templates.type.TUNNEL": "Tunnel / Bündelung",
  "templates.typeDesc.TUNNEL": "SpeedFusion-Bündelung und VPN-Client-Profile",
  "templates.step.TUNNEL.profile": "Tunnelprofil",
  "templates.stepDesc.TUNNEL.profile": "Gegenstelle und Bündelungsmodus",
  "templates.field.TUNNEL.name": "Profilname",
  "templates.field.TUNNEL.peerHost": "Host der Gegenstelle",
  "templates.help.TUNNEL.peerHost": "Hostname oder IP-Adresse des Tunnelendpunkts",
  "templates.field.TUNNEL.mode": "Modus",
  "templates.option.TUNNEL.mode.bonding": "Bündelung – Bandbreite kombinieren",
  "templates.option.TUNNEL.mode.failover": "Failover – heiße Reserve",
  "templates.option.TUNNEL.mode.overflow": "Überlauf – auf die zweite Verbindung",
  "templates.field.TUNNEL.encryption": "Verschlüsselung",
  "templates.option.TUNNEL.encryption.aes256": "AES-256",
  "templates.option.TUNNEL.encryption.aes128": "AES-128",
  "templates.option.TUNNEL.encryption.none": "Keine",
  "templates.step.TUNNEL.behaviour": "Verhalten",
  "templates.stepDesc.TUNNEL.behaviour": "Wiederverbindung und VPN-Client",
  "templates.field.TUNNEL.autoReconnect": "Automatisch neu verbinden",
  "templates.field.TUNNEL.vpnClientEnabled": "VPN-Client",
  "templates.field.TUNNEL.vpnClientConfig": "VPN-Profil",
  "templates.type.SYSTEM": "Systemgrundlage",
  "templates.typeDesc.SYSTEM": "Zeit, Identität und Firmware-Richtlinie",
  "templates.step.SYSTEM.identity": "Identität und Zeit",
  "templates.stepDesc.SYSTEM.identity": "Benennung und Uhr",
  "templates.field.SYSTEM.hostname": "Muster für den Hostnamen",
  "templates.help.SYSTEM.hostname": "Mit {site} wird der Standortname eingefügt",
  "templates.field.SYSTEM.timezone": "Zeitzone",
  "templates.field.SYSTEM.ntp": "NTP-Server",
  "templates.step.SYSTEM.maintenance": "Wartung",
  "templates.stepDesc.SYSTEM.maintenance": "Firmware und Sicherungen",
  "templates.field.SYSTEM.firmwareCheck": "Firmware beim Anwenden prüfen",
  "templates.field.SYSTEM.firmwareUpgrade": "Updates automatisch installieren",
  "templates.help.SYSTEM.firmwareUpgrade": "Installiert die neueste Version – löst einen Neustart aus",
  "templates.field.SYSTEM.backup": "Vor Änderungen sichern",
  "templates.type.LINK": "Funkstrecke",
  "templates.typeDesc.LINK": "Funkeinstellungen für Punkt-zu-Punkt oder Punkt-zu-Mehrpunkt – eine Vorlage pro Seite",
  "templates.step.LINK.role": "Rolle",
  "templates.stepDesc.LINK.role": "Welches Ende der Strecke dieses Gerät ist",
  "templates.field.LINK.topology": "Topologie",
  "templates.option.LINK.topology.ptp": "Punkt-zu-Punkt – eine Station",
  "templates.option.LINK.topology.ptmp": "Punkt-zu-Mehrpunkt – mehrere Stationen",
  "templates.field.LINK.role": "Diese Seite",
  "templates.help.LINK.role": "Wenden Sie zuerst die Stationsvorlage an und dann innerhalb von 10 Minuten den Access Point – umgekehrt verlieren die Stationen die Verbindung. Eine Station behält ihre neuen Einstellungen, sobald der Access Point passt; geschieht das nicht rechtzeitig, setzt sie sich selbst zurück",
  "templates.option.LINK.role.ap": "Access Point (der Sektor / das nahe Ende)",
  "templates.option.LINK.role.station": "Station (das CPE / das ferne Ende)",
  "templates.field.LINK.interface": "Funkschnittstelle",
  "templates.help.LINK.interface": "Leer wählt die Funkstrecken-Radio, wenn es eine gibt (eine Station, eine 60-GHz- oder NV2/Nstreme-Radio); ein Gerät mit mehreren Radios braucht den Namen (wlan60-1, wlan1, wifi1 …)",
  "templates.step.LINK.radio": "Funk",
  "templates.stepDesc.LINK.radio": "Beide Enden müssen übereinstimmen",
  "templates.field.LINK.ssid": "Streckenname (SSID)",
  "templates.field.LINK.linkKey": "Streckenschlüssel",
  "templates.help.LINK.linkKey": "Mindestens 8 Zeichen. NV2-Schlüssel, WPA2/WPA3-Passphrase oder 60-GHz-Passwort, je nach Radio",
  "templates.field.LINK.frequency": "Frequenz",
  "templates.help.LINK.frequency": "5 GHz z. B. 5500. 60 GHz: 58320, 60480, 62640, 64800 oder 66000. Leer behält die aktuelle",
  "templates.field.LINK.channelWidth": "Kanalbreite",
  "templates.help.LINK.channelWidth": "Bei 60 GHz nicht verwendet",
  "templates.option.LINK.channelWidth.20": "20 MHz",
  "templates.option.LINK.channelWidth.40": "40 MHz",
  "templates.option.LINK.channelWidth.80": "80 MHz",
  "templates.field.LINK.protocol": "Protokoll",
  "templates.help.LINK.protocol": "Nur der 2,4/5-GHz-Treiber „wireless“; WLAN-6-Radios verwenden immer 802.11",
  "templates.option.LINK.protocol.nv2": "NV2 (MikroTik TDMA – am besten für Außenstrecken)",
  "templates.option.LINK.protocol.802.11": "802.11 (Standard-WLAN)",
  "templates.option.LINK.protocol.nstreme": "Nstreme",
  "templates.option.LINK.protocol.any": "Beliebig (dem Access Point folgen)",
  "templates.field.LINK.txPower": "Sendeleistung",
  "templates.help.LINK.txPower": "Leer behält die aktuelle Einstellung. Bleiben Sie innerhalb des Grenzwerts Ihrer Regulierungsdomäne",
  "templates.type.CYBERSECURE": "CyberSecure",
  "templates.typeDesc.CYBERSECURE": "IPS, Inhaltsfilter und Bedrohungsabwehr",
  "templates.step.CYBERSECURE.ips": "Angriffsabwehr",
  "templates.stepDesc.CYBERSECURE.ips": "Blockieren anhand von Signaturen",
  "templates.field.CYBERSECURE.ips.enabled": "IPS/IDS einschalten",
  "templates.field.CYBERSECURE.ips.mode": "Modus",
  "templates.option.CYBERSECURE.ips.mode.ips": "IPS – Bedrohungen blockieren",
  "templates.option.CYBERSECURE.ips.mode.ids": "IDS – nur melden",
  "templates.step.CYBERSECURE.content": "Inhaltsfilter",
  "templates.stepDesc.CYBERSECURE.content": "Sperren nach Kategorie und Domain",
  "templates.field.CYBERSECURE.contentFilter.enabled": "Inhaltsfilter einschalten",
  "templates.field.CYBERSECURE.contentFilter.categories": "Gesperrte Kategorien",
  "templates.option.CYBERSECURE.contentFilter.categories.adult": "🔞 Erwachsene",
  "templates.option.CYBERSECURE.contentFilter.categories.gambling": "🎰 Glücksspiel",
  "templates.option.CYBERSECURE.contentFilter.categories.malware": "🦠 Malware",
  "templates.option.CYBERSECURE.contentFilter.categories.p2p": "⬇️ P2P",
  "templates.option.CYBERSECURE.contentFilter.categories.ads": "📢 Werbung",
  "templates.option.CYBERSECURE.contentFilter.categories.social": "💬 Soziale Medien",
  "templates.option.CYBERSECURE.contentFilter.categories.streaming": "📺 Streaming",
  "templates.option.CYBERSECURE.contentFilter.categories.games": "🎮 Spiele",
  "templates.field.CYBERSECURE.contentFilter.customBlockedUrls": "Eigene gesperrte Domains",
  "templates.help.CYBERSECURE.contentFilter.customBlockedUrls": "Fügen Sie Domains einzeln hinzu",
  "templates.type.COMPANY_DEFAULTS": "Unternehmensstandards",
  "templates.typeDesc.COMPANY_DEFAULTS": "Ein Bündel von Vorlagen, das auf jeden neuen Standort angewendet wird",
  "templates.step.COMPANY_DEFAULTS.bundle": "Enthaltene Vorlagen",
  "templates.stepDesc.COMPANY_DEFAULTS.bundle": "Der Reihe nach angewendet, wenn ein Standort online geht",
  "templates.field.COMPANY_DEFAULTS.networkTemplateIds": "Netzwerkvorlagen",
  "templates.field.COMPANY_DEFAULTS.wifiTemplateIds": "WLAN-Vorlagen",
  "templates.field.COMPANY_DEFAULTS.wanTemplateId": "WAN-Vorlage",
  "templates.field.COMPANY_DEFAULTS.cellularTemplateId": "Mobilfunkvorlage",
  "templates.field.COMPANY_DEFAULTS.tunnelTemplateId": "Tunnelvorlage",
  "templates.field.COMPANY_DEFAULTS.systemTemplateId": "Systemvorlage",
}

export default templates
